import { memo, useEffect } from 'react'
import { AnimatePresence } from 'framer-motion'
import { GlobalEvents } from '@highlight/ipc'
import { useGlobalEvent } from '@highlight/ipc/react'
import { useOpenModals, useModals } from './atoms'
import { ModalState } from './types'
import { ModalContext } from './nested/modal-context'
import { modalRegistry } from './modal-registry'

export const ModalRenderer = memo(() => {
  const modals = useOpenModals()
  const { openModal } = useModals()

  // Global event listener for opening hotkeys modal from other windows (e.g., overlay)
  useGlobalEvent('open-hotkeys-modal', () => {
    openModal('hotkeys-overview')
  })

  useGlobalEvent('open-changelog-modal', (payload?: { useLatest?: boolean }) => {
    openModal('changelog', { useLatest: !!payload?.useLatest })
  })

  // Signal that all modal listeners are ready.
  // This runs after useGlobalEvent registers its listener.
  useEffect(() => {
    GlobalEvents.emit('settings-window-ready')
  }, [])

  return (
    <AnimatePresence mode="sync">
      {modals.map((modal) => (
        <ModalWrapper key={modal.id} modal={modal} />
      ))}
    </AnimatePresence>
  )
})

ModalRenderer.displayName = 'ModalRenderer'

const ModalWrapper = memo(({ modal }: { modal: ModalState }) => {
  const { closeModal } = useModals()

  const modalId = modal.id as keyof typeof modalRegistry
  const modalDef = modalRegistry[modalId]
  if (!modalDef) {
    console.warn('Unsupported modal id:', modal.id)
    return null
  }

  const ModalComponent = modalDef.render

  return (
    <ModalContext.Provider
      value={{
        id: modal.id,
        context: modal.context,
        close: () => closeModal(modalId),
        confirm: modal.confirm ?? modalDef.confirm,
      }}
    >
      <ModalComponent />
    </ModalContext.Provider>
  )
})

ModalWrapper.displayName = 'ModalWrapper'
